<template>
  <span class="agent-entity-label">
    <!-- resolved (found: true) → human label; deep-link only in detail (non-compact) -->
    <template v-if="resolved">
      <router-link
        v-if="primaryRoute && !compact"
        :to="primaryRoute"
        class="primary--text routerLink font-weight-medium"
        @click.native.stop
      >{{ primaryText }}</router-link>
      <span v-else class="text--secondary font-weight-medium">{{ primaryText }}</span>

      <!-- customer (deep-links to the customer in detail) for invoice / subscription / payment / source -->
      <template v-if="customerName">
        <span class="text--disabled"> · </span>
        <router-link
          v-if="customerLink && !compact"
          :to="customerLink"
          class="primary--text routerLink"
          @click.native.stop
        >{{ customerName }}</router-link>
        <span v-else class="text--secondary">{{ customerName }}</span>
      </template>

      <!-- secondary detail (tax_id / next billing) -->
      <span v-if="!compact && secondaryText" class="text--disabled"> · {{ secondaryText }}</span>

      <!-- status badge (detail only — avoids clashing with the card's own status) -->
      <v-chip
        v-if="statusInfo && !compact"
        class="ml-2"
        :color="statusInfo.color"
        :text-color="statusInfo.textColor"
        label
        x-small
      >{{ statusInfo.label }}</v-chip>
    </template>

    <!-- not found (found: false) → muted id, no link -->
    <span v-else-if="notFound" class="text--disabled" title="No disponible">{{ entity.label || entity.id }}</span>

    <!-- entity null → fallback to raw entity_type · entity_id (current behaviour) -->
    <span v-else-if="fallbackText" class="text--disabled">{{ fallbackText }}</span>
  </span>
</template>
<script>
import GenericViewMixin from '@/mixins/GenericViewMixin'

/**
 * Route name for the detail view of each resolvable entity type.
 * `source` (card) has no own detail page, so it falls back to the customer chip.
 * @type {Object<string, string>}
 */
const ROUTE_BY_TYPE = {
  customer: 'CustomerRetrieveUpdate',
  invoice: 'InvoiceRetrieve',
  subscription: 'SubscriptionRetrieve',
  payment: 'PaymentRetrieve'
}

/**
 * Spanish label + Vuetify colors for the polymorphic entity statuses
 * (invoice / payment / subscription / source). Falls back to no badge
 * when the status is unknown.
 * @type {Object<string, { label: string, color: string, textColor: string }>}
 */
const ENTITY_STATUS = {
  // invoice
  draft: { label: 'Borrador', color: 'grey lighten-3', textColor: 'grey darken-1' },
  finalized: { label: 'Emitida', color: 'blue lighten-5', textColor: 'blue darken-2' },
  paid: { label: 'Pagada', color: 'green lighten-5', textColor: 'green darken-2' },
  uncollectible: { label: 'Incobrable', color: 'red lighten-5', textColor: 'red darken-2' },
  void: { label: 'Anulada', color: 'defaultGrey', textColor: 'secondary' },
  // payment
  succeeded: { label: 'Exitoso', color: 'green lighten-5', textColor: 'green darken-2' },
  failed: { label: 'Fallido', color: 'red lighten-5', textColor: 'red darken-2' },
  refunded: { label: 'Reembolsado', color: 'blue-grey lighten-5', textColor: 'blue-grey darken-1' },
  // subscription
  active: { label: 'Activa', color: 'green lighten-5', textColor: 'green darken-2' },
  trialing: { label: 'En prueba', color: 'blue lighten-5', textColor: 'blue darken-2' },
  past_due: { label: 'Vencida', color: 'amber lighten-5', textColor: 'amber darken-4' },
  paused: { label: 'Pausada', color: 'orange lighten-5', textColor: 'orange darken-2' },
  cancelled: { label: 'Cancelada', color: 'defaultGrey', textColor: 'secondary' },
  // shared (payment/source/subscription)
  pending: { label: 'Pendiente', color: 'amber lighten-5', textColor: 'amber darken-4' },
  // source (card)
  chargeable: { label: 'Vigente', color: 'green lighten-5', textColor: 'green darken-2' },
  unverified: { label: 'Sin verificar', color: 'amber lighten-5', textColor: 'amber darken-4' },
  expired: { label: 'Expirada', color: 'red lighten-5', textColor: 'red darken-2' }
}

export default {
  name: 'EntityLabel',
  mixins: [
    GenericViewMixin
  ],
  props: {
    // expanded entity object from the API (?expand=entity), or null
    entity: {
      type: Object,
      default: null
    },
    // raw fallbacks present on the action/decision/insight/inbox-item
    entityType: {
      type: String,
      default: null
    },
    entityId: {
      type: String,
      default: null
    },
    // compact mode for caption contexts (board cards, inbox): hides secondary detail
    compact: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    resolved () {
      return !!(this.entity && this.entity.found)
    },
    notFound () {
      return !!(this.entity && this.entity.found === false)
    },
    fallbackText () {
      return this.entityType ? `${this.entityType} · ${this.entityId}` : ''
    },
    primaryText () {
      const e = this.entity
      if (!e) return ''
      if (e.type === 'payment' && e.amount != null) {
        return `${this.$options.filters.currency(e.amount, e.currency)} ${e.currency || ''}`.trim()
      }
      return e.label || e.id
    },
    primaryRoute () {
      const e = this.entity
      if (!e) return null
      const name = ROUTE_BY_TYPE[e.type]
      return name ? { name, params: { id: e.id } } : null
    },
    customerName () {
      const e = this.entity
      if (!e || e.type === 'customer' || !e.customer) return null
      return e.customer.name
    },
    customerLink () {
      const e = this.entity
      if (!e || !e.customer || !e.customer.id) return null
      return { name: 'CustomerRetrieveUpdate', params: { id: e.customer.id } }
    },
    secondaryText () {
      const e = this.entity
      if (!e) return null
      if (e.type === 'customer' && e.tax_id) return e.tax_id
      if (e.type === 'subscription' && e.next_billing) return `Próx. cobro ${this.$options.filters.date(e.next_billing)}`
      return null
    },
    statusInfo () {
      const e = this.entity
      if (!e || !e.status) return null
      return ENTITY_STATUS[e.status] || null
    }
  }
}
</script>
